import useRegisterStore from '@/hooks/useRegistrationStore';
import {
  isMinorConfirmConsent,
  isOptionSelectConsent,
} from '@/utils/consentUtils';
import { ConsentRecord } from '@viome/viome-base/src/usecase/Register/Register.model';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import useConfirmRegister from './useConfirmRegister';

const useUserConsentController = (getCheckboxValues?: () => Record<string, string>) => {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [consentRecords, setConsentRecords] = useState<ConsentRecord[]>([]);

  const { consents } = useRegisterStore();

  const { onSubmit, registrationPending } = useConfirmRegister();


  const isLoading = !consents || consents.length === 0;

  const form = useForm();
  const { control, formState, getValues, reset, watch, handleSubmit } = form;

  const consentData = !isLoading ? consents[index] : null;
  const isMinor = consentData ? isMinorConfirmConsent(consentData) : false;
  const isRadioButton = consentData
    ? isOptionSelectConsent(consentData)
    : false;
  const isCheckbox = consentData ? !isMinor && !isRadioButton : false;
  const isLastStep = index === consents.length - 1;

  useEffect(() => {
    if (consentData) {
      const existingRecordsForStep = consentRecords.filter(
        (record) => record.systemId === consentData.systemId,
      );

      let defaultValues: Record<string, any> = { consentChoice: undefined };

      if (isCheckbox) {
        defaultValues = consentData.choices?.reduce<Record<string, boolean>>(
          (acc, choice) => {
            acc[choice.recordValue] =
              existingRecordsForStep.some(
                (r) => r.recordValue === choice.recordValue,
              ) || false;
            return acc;
          },
          {},
        );
      } else if (isRadioButton && existingRecordsForStep.length > 0) {
        defaultValues.consentChoice = existingRecordsForStep[0].recordValue;
      }

      reset(defaultValues);
    }
  }, [index, consentData, reset, isCheckbox, consentRecords]);

  const watchedChoiceValue = watch('consentChoice');
  useEffect(() => {
    if (isRadioButton && watchedChoiceValue) {
      const selectedChoice = consentData?.choices.find(
        (c) => c.recordValue === watchedChoiceValue,
      );
      setErrorMessage(selectedChoice?.errorMessage || null);
    } else {
      setErrorMessage(null);
    }
  }, [watchedChoiceValue, isRadioButton, consentData]);

  const onClickGoBack = () => {
    if (index > 0) {
      setIndex(index - 1);
    } else {
      navigate(-1);
    }
  };

  const onNext = () => {
    if (!consentData) return;

    let newRecords: ConsentRecord[] = [];

    if (isRadioButton) {
      const values = getValues();
      newRecords.push({
        systemId: consentData.systemId,
        recordValue: values.consentChoice,
      });
    } else if (isCheckbox && getCheckboxValues) {
      const checkboxValues = getCheckboxValues();
      newRecords =
        consentData.choices
          ?.filter((choice) => checkboxValues[choice.ordinal])
          .map((choice) => ({
            systemId: consentData.systemId,
            recordValue: choice.recordValue,
          })) || [];
    } else {
      // isMinor or acknowledgment
      newRecords.push({
        systemId: consentData.systemId,
        recordValue: 'true',
      });
    }

    const otherRecords = consentRecords.filter(
      (record) => record.systemId !== consentData.systemId,
    );

    if (!isLastStep) {
      setConsentRecords([...otherRecords, ...newRecords]);
      setIndex(index + 1);
    } else {
      const finalRecords = [...otherRecords, ...newRecords];
      onSubmit(finalRecords);
    }
  };

  const onClickNext = isCheckbox ? onNext : (isRadioButton ? handleSubmit(onNext) : onNext);

  const onDownloadConsentPress = async () => {
    const link = `https://static.azure.viome.com/viome-cdn/media/app/consent_form/${consentData?.downloadLink}`;
    window.open(link, '_blank');
  };

  if (isLoading) {
    return {
      isLoading: true,
      form,
      consentData: null,
      isLastStep: false,
      isMinor: false,
      isRadioButton: false,
      isCheckbox: false,
      errorMessage: null,
      onClickGoBack: () => { },
      onClickNext: () => { },
      onDownloadConsentPress: () => { },
      isPendingRegistration: false,
    };
  }

  const consentFormTitle = isMinor ? 'Minor Consent' : '';

  return {
    isLoading,
    form,
    consentData,
    consentFormTitle,
    control,
    isLastStep,
    isMinor,
    isRadioButton,
    isCheckbox,
    isValid: formState.isValid,
    errorMessage,
    onClickGoBack,
    onClickNext,
    isPendingRegistration: registrationPending,
    onDownloadConsentPress,
  };
};

export default useUserConsentController;
